import { createContext, useContext } from 'react'
import type { AuthMode } from '@/config/env'

export const AUTH_CALLBACK_PATH = '/auth/callback'

/** `signing_out`: logout em curso (não disparar novo login enquanto o redirect acontece). */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error' | 'signing_out'

/** Perfil exibido no shell, vindo do id token (OIDC) ou da persona mock. */
export interface SessionProfile {
  name: string
  email: string | null
  username: string | null
  /** Claim `picture` do id token, quando houver (avatar; validado no <Avatar>). */
  picture?: string | null
}

/**
 * Contrato de sessão independente da lib de OIDC. O resto do app (http, realtime, shell)
 * depende só disto, então trocar react-oidc-context ou o modo mock não vaza para as features.
 */
export interface AuthSession {
  mode: AuthMode
  status: AuthStatus
  profile: SessionProfile | null
  error: Error | null
  getAccessToken: () => string | null
  signIn: (returnTo?: string) => Promise<void>
  signOut: () => Promise<void>
  /** Renovação silenciosa do token (ex.: tempo real recusou por expiração). */
  renewToken: () => Promise<void>
  /** Destino salvo antes do login (usado pela rota /auth/callback). Sempre um caminho interno. */
  returnTo: () => string
}

export const AuthSessionContext = createContext<AuthSession | null>(null)

export function useAuthSession(): AuthSession {
  const session = useContext(AuthSessionContext)
  if (!session) throw new Error('useAuthSession precisa estar dentro de <AuthSessionProvider>.')
  return session
}

/** Estado salvo no `state` do OIDC para voltar à página de origem após o login. */
export interface SigninState {
  returnTo: string
}

export function isSigninState(value: unknown): value is SigninState {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { returnTo?: unknown }).returnTo === 'string'
  )
}

/** Aceita só caminhos internos (evita open redirect via state adulterado). */
export function safeReturnTo(value: unknown): string {
  if (!isSigninState(value)) return '/'
  const { returnTo } = value
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) return '/'
  if (returnTo.startsWith('/auth/')) return '/'
  return returnTo
}
